import { useMemo } from "react";
import {
    FinancialDocumentView,
    FinancialDocumentViewSchema,
} from "@common/types/views/financialDocumentViews";
import { PersistableView } from "@common/types/views/views";
import { FinancialDocumentsTaskTable } from "@components/tasks/TaskBody/FinancialDocuments/FinancialDocumentsTaskTable";
import { getFinancialDocumentsTaskFilterTabs } from "@components/tasks/TaskBody/FinancialDocuments/financialDocumentsTaskFilters";
import { parseFinancialDocumentsTaskView } from "@components/tasks/TaskBody/FinancialDocuments/financialDocumentsTaskView";
import { withoutBulkActions } from "@components/tasks/TaskBody/shared/taskTableView";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import styles from "./CreateTaskDrawer.module.scss";
import { useTaskCreationPreviewView } from "./useTaskCreationPreviewView";

interface TaskCreationFinancialDocumentsPreviewProps {
    viewConfig: PersistableView;
}

interface PreviewBodyProps {
    initialView: FinancialDocumentView;
    isSelection: boolean;
}

const renderEmptyPreview = () => (
    <EmptyState header="No documents match this task" />
);

const PreviewBody = ({ initialView, isSelection }: PreviewBodyProps) => {
    const { view, handleFiltersChange, handleViewChange } =
        useTaskCreationPreviewView({
            initialView,
            viewSchema: FinancialDocumentViewSchema,
        });

    const availableFilters = useMemo(
        () =>
            isSelection
                ? []
                : getFinancialDocumentsTaskFilterTabs({
                      isWorkspaceScoped: true,
                  }),
        [isSelection],
    );

    return (
        <FinancialDocumentsTaskTable
            view={view}
            onViewChange={handleViewChange}
            onFiltersChange={handleFiltersChange}
            availableFilters={availableFilters}
            defaultView={initialView}
            emptyView={renderEmptyPreview}
        />
    );
};

export const TaskCreationFinancialDocumentsPreview = ({
    viewConfig,
}: TaskCreationFinancialDocumentsPreviewProps) => {
    const initialView = useMemo(
        () => withoutBulkActions(parseFinancialDocumentsTaskView(viewConfig)),
        [viewConfig],
    );
    const isSelection = !!viewConfig.filters.financialDocumentIds?.length;

    return (
        <div className={styles.previewPane}>
            <div className={styles.previewSurface}>
                <PreviewBody
                    initialView={initialView}
                    isSelection={isSelection}
                />
            </div>
        </div>
    );
};
